import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Player from "@vimeo/player";
import { Maximize, Pause, Play, RotateCcw, Share2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ReelPlayer({ videoUrl, poster, testIdPrefix = "player", share, onDuration }) {
  const hostRef = useRef(null);
  const barRef = useRef(null);
  const hideTimer = useRef(null);
  const playerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [error, setError] = useState("");
  const endingRef = useRef(false);
  const durationRef = useRef(0);

  useEffect(() => {
    const player = new Player(hostRef.current, {
      url: videoUrl,
      controls: false,
      dnt: true,
      title: false,
      byline: false,
      portrait: false,
      pip: false,
      responsive: true,
    });
    playerRef.current = player;

    player
      .ready()
      .then(() => {
        setReady(true);
        return player.getDuration();
      })
      .then((d) => {
        setDuration(d);
        durationRef.current = d;
        if (onDuration) onDuration(d);
      })
      .catch(() => setError("The video could not be loaded. Please check back soon."));

    player.on("play", () => {
      endingRef.current = false;
      setPlaying(true);
      setStarted(true);
      setEnded(false);
    });
    player.on("pause", () => setPlaying(false));
    player.on("ended", () => {
      setPlaying(false);
      setEnded(true);
    });
    player.on("timeupdate", (data) => {
      setTime(data.seconds);
      const d = durationRef.current;
      if (d && data.seconds >= d - 1.2 && !endingRef.current) {
        endingRef.current = true;
        setEnded(true);
      }
    });
    player.on("volumechange", ({ volume }) => setMuted(volume === 0));

    return () => {
      clearTimeout(hideTimer.current);
      player.destroy().catch(() => {});
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  const pokeControls = () => {
    setControlsVisible(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (playerRef.current) setControlsVisible(false);
    }, 2600);
  };

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p) return;
    pokeControls();
    (playing ? p.pause() : p.play()).catch(() =>
      setError("Playback was blocked — press play once more.")
    );
  };

  const replay = () => {
    const p = playerRef.current;
    if (!p) return;
    p.setCurrentTime(0)
      .then(() => p.play())
      .catch(() => {});
  };

  const seekFromEvent = (e) => {
    const p = playerRef.current;
    if (!p || !barRef.current || !duration) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const seconds = ratio * duration;
    setTime(seconds);
    p.setCurrentTime(seconds).catch(() => {});
  };

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;
    pokeControls();
    p.setVolume(muted ? 1 : 0).catch(() => {});
  };

  const goFullscreen = () => {
    playerRef.current?.requestFullscreen().catch(() => {});
  };

  const handleShare = async () => {
    const url = share?.url || `${window.location.origin}/#showreel`;
    const shareData = {
      title: share?.title || "Bite",
      text: share?.text || "Watch this on Bite.",
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: "Share it with anyone." });
      return;
    } catch {
      // fall through to legacy copy
    }
    try {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (!ok) throw new Error("copy failed");
      toast.success("Link copied", { description: "Share it with anyone." });
    } catch {
      toast.error("Couldn't copy the link", { description: url });
    }
  };

  const progress = duration ? (time / duration) * 100 : 0;
  const showOverlay = !started || ended;

  return (
    <div>
      <div
        data-testid={`${testIdPrefix}-player`}
        onMouseMove={pokeControls}
        className="group relative aspect-video w-full overflow-hidden border border-white/10 bg-black"
      >
        <div ref={hostRef} className="absolute inset-0 h-full w-full overflow-hidden" />

        <AnimatePresence>
          {showOverlay && (
            <motion.button
              key="poster"
              type="button"
              onClick={ended ? replay : togglePlay}
              data-testid={`${testIdPrefix}-play-overlay`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="absolute inset-0 z-10 flex cursor-pointer flex-col items-center justify-center gap-8"
            >
              <img
                src={poster}
                alt="Video poster — cinematic frame from a Bite production"
                className="absolute inset-0 h-full w-full object-cover opacity-50"
              />
              <span className="absolute inset-0 bg-black/60" />
              <span className="relative flex h-20 w-20 items-center justify-center rounded-full border border-white/40 transition-colors duration-300 hover:border-[#E1A730] hover:bg-[#E1A730]/10 md:h-24 md:w-24">
                {ended ? (
                  <RotateCcw className="h-6 w-6 text-white" />
                ) : (
                  <Play className="h-6 w-6 translate-x-0.5 text-white" fill="currentColor" />
                )}
              </span>
              <span className="relative font-mono text-[10px] tracking-[0.45em] text-zinc-300">
                {ended ? "REPLAY" : ready ? "PLAY" : "LOADING"}
              </span>
            </motion.button>
          )}
        </AnimatePresence>

        <div
          data-testid={`${testIdPrefix}-controls`}
          className={`absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/85 to-transparent px-4 pb-4 pt-12 transition-opacity duration-300 md:px-6 ${
            controlsVisible && started && !ended ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="flex items-center gap-4 md:gap-5">
            <button
              type="button"
              onClick={togglePlay}
              data-testid={`${testIdPrefix}-play-pause-button`}
              aria-label={playing ? "Pause" : "Play"}
              className="text-white transition-colors duration-300 hover:text-[#E1A730]"
            >
              {playing ? (
                <Pause className="h-4 w-4" fill="currentColor" />
              ) : (
                <Play className="h-4 w-4" fill="currentColor" />
              )}
            </button>

            <div
              ref={barRef}
              data-testid={`${testIdPrefix}-seek-bar`}
              role="slider"
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={Math.round(duration)}
              aria-valuenow={Math.round(time)}
              onPointerDown={(e) => {
                barRef.current.setPointerCapture(e.pointerId);
                setDragging(true);
                seekFromEvent(e);
                pokeControls();
              }}
              onPointerMove={(e) => {
                if (dragging) seekFromEvent(e);
              }}
              onPointerUp={() => setDragging(false)}
              className="group/bar relative flex h-6 flex-1 cursor-pointer items-center"
            >
              <div className="h-px w-full bg-white/25">
                <div
                  className="h-full bg-[#E1A730] transition-[width] duration-100"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div
                className="absolute h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-[#E1A730] opacity-0 transition-opacity duration-200 group-hover/bar:opacity-100"
                style={{ left: `${progress}%` }}
              />
            </div>

            <span
              data-testid={`${testIdPrefix}-time`}
              className="w-20 text-right font-mono text-[10px] tabular-nums tracking-[0.2em] text-zinc-300"
            >
              {formatTime(time)} / {formatTime(duration)}
            </span>

            <button
              type="button"
              onClick={toggleMute}
              data-testid={`${testIdPrefix}-mute-button`}
              aria-label={muted ? "Unmute" : "Mute"}
              className="text-white transition-colors duration-300 hover:text-[#E1A730]"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>

            {share && (
              <button
                type="button"
                onClick={handleShare}
                data-testid={`${testIdPrefix}-share-icon-button`}
                aria-label="Share"
                className="text-white transition-colors duration-300 hover:text-[#E1A730]"
              >
                <Share2 className="h-4 w-4" />
              </button>
            )}

            <button
              type="button"
              onClick={goFullscreen}
              data-testid={`${testIdPrefix}-fullscreen-button`}
              aria-label="Fullscreen"
              className="text-white transition-colors duration-300 hover:text-[#E1A730]"
            >
              <Maximize className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <p data-testid={`${testIdPrefix}-error`} role="alert" className="mt-4 text-sm text-zinc-500">
          {error}
        </p>
      )}
    </div>
  );
}
